<template>
  <div class="text-lg lg:text-xl" :class="paragraphClassList">
    <div
      class="bg-lightGreen overflow-hidden col-span-4 sm:col-span-6 md:col-span-6 md:col-start-2 lg:col-span-8 lg:col-start-3 flex flex-col sm:flex-row"
    >
      <MediaImage
        v-if="media"
        v-bind="media"
        :image-style="imageStyle"
        hide-caption
        class="object-cover size-full flex-1 sm:w-1/2"
        :img-class="'h-full object-cover'"
      />
      <div
        v-else-if="location && location.address?.mapImage?.url"
        class="size-full flex-1 sm:w-1/2"
      >
        <a
          v-if="mapsHref"
          :href="mapsHref"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            alt="maps image"
            :src="location.address.mapImage?.url"
            class="size-full object-cover"
          />
        </a>
        <img
          v-else
          alt="maps image"
          :src="location.address.mapImage?.url"
          class="size-full object-cover"
        />
      </div>
      <div class="p-9 flex-1 bg-lightGreen">
        <h2
          v-if="title"
          v-blokkli-editable:field_title
          class="mb-2 text-xl md:text-3xl text-darkGreen"
        >
          {{ title }}
        </h2>
        <div
          v-if="text"
          v-blokkli-editable:field_text
          class="ck-content hyphens-auto mt-5 text-darkGreen whitespace-pre-wrap"
          v-html="text"
        />
        <div v-if="email || phone" class="mt-5 text-darkGreen space-y-2">
          <div v-if="email">
            <a :href="`mailto:${email}`" class="underline break-all">{{
              email
            }}</a>
          </div>
          <div v-if="phone">
            <a :href="telHref" class="underline">{{ phone }}</a>
          </div>
        </div>

        <div class="mt-5 whitespace-pre-wrap text-darkGreen">
          {{ description }}
        </div>

        <VuepalLink
          v-if="link"
          :to="link.uri?.path"
          class="button is-filled mt-10"
        >
          {{ link.linkTitle }}

          <SpriteSymbol name="arrow-right" class="size-6" />
        </VuepalLink>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { ParagraphContactFragment } from '#graphql-operations'

const props = defineProps<{ taxonomy?: ParagraphContactFragment['taxonomy'] }>()

const { options, parentType } = defineBlokkli({
  bundle: 'contact',
  editor: {
    addBehaviour: 'no-form',
    editTitle: (el) => el.textContent,
    getDraggableElement: (el) => el.querySelector('.ck-content'),
    determineVisibleOptions: (ctx) => {
      if (!ctx.parentType) {
        return ['spacing']
      }
      return []
    },
  },
  globalOptions: ['spacing'],
})

const title = computed(() => {
  if (referencedTax?.value?.name) {
    return referencedTax.value.name
  }
  return ''
})

const description = computed(() => {
  return referencedTax.value?.description ?? ''
})

const referencedTax = computed(() => {
  if (
    props.taxonomy?.__typename === 'TaxonomyTermLocation' ||
    props.taxonomy?.__typename === 'TaxonomyTermPerson'
  ) {
    return props.taxonomy
  }
  return null
})

const person = computed(() => {
  if (props.taxonomy?.__typename === 'TaxonomyTermPerson') {
    return props.taxonomy
  }
  return null
})

const location = computed(() => {
  if (props.taxonomy?.__typename === 'TaxonomyTermLocation') {
    return props.taxonomy
  }
  return null
})

const text = computed(() => {
  let info = ''
  if (person?.value?.function) {
    info += person.value.function + '\n'
  }
  if (referencedTax?.value?.department) {
    info += referencedTax.value.department + '\n'
  }
  if (person?.value?.area) {
    info += person.value.area + '\n'
  }
  if (location?.value?.address) {
    info += location.value.address?.addressLine1 + '\n'
    if (location.value.address?.addressLine2) {
      info += location.value.address?.addressLine2 + '\n'
    }
    info += location.value.address?.postalCode + ' '
    info += location.value.address?.locality + '\n'
  }
  return info
})

const email = computed(() => referencedTax.value?.email ?? '')
const phone = computed(() => referencedTax.value?.phone ?? '')

const telHref = computed(() =>
  phone.value ? `tel:${phone.value.replace(/\s+/g, '')}` : '',
)

const mapsHref = computed(() => {
  const addr = location.value?.address
  const parts: string[] = []
  if (addr?.addressLine1) parts.push(addr.addressLine1)
  if (addr?.addressLine2) parts.push(addr.addressLine2)
  if (addr?.postalCode) parts.push(addr.postalCode)
  if (addr?.locality) parts.push(addr.locality)
  const q = parts.join(' ')
  return q
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`
    : ''
})

const media = computed(() => {
  if (person?.value?.image) {
    return person.value.image
  }
  return undefined
})

const link = computed(() => {
  if (referencedTax?.value?.link) {
    return referencedTax.value.link
  }
  return undefined
})

const paragraphClassList = computed(() => {
  const classList = []

  if (options.value.spacing === 'small') {
    classList.push('pt-6', 'lg:pt-10')
  } else if (options.value.spacing === 'large') {
    classList.push('pt-12', 'lg:pt-20')
  }

  if (parentType.value === 'accordeon') {
    classList.push('mb-5')
  } else if (!parentType.value) {
    classList.push('grid-container')
  }

  return classList
})

const imageStyle = defineImageStyle({
  type: 'sizes',
  aspectRatio: 456 / 385,
  sizes: {
    xs: 608,
    sm: 360,
    md: 304,
    lg: 456,
  },
})
</script>
